import { Tooltip, percent } from "@amcharts/amcharts5";
import { AxisRendererX, AxisRendererY, CategoryAxis, ColumnSeries, XYChart } from "@amcharts/amcharts5/xy";
import { keys, omit, pick, values } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import Lang from "@/lang";
import { waffleDefaults } from "@/settings/charts/waffleChart";
import { chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

type PercentsBrickData = { percents: number } & { [key: string]: string | number };

type GroupedPercentsBrickData = Record<string, { percents: number; data: PercentsBrickData | null }>;

export default class WaffleChart extends BaseChart {
  emptyCategories = [] as string[];
  MAX_AMOUNT_OF_PERCENTS = 100;

  initChart() {
    this.root.container.children.clear();
    this.root.interfaceColors.set("grid", undefined);

    this.createChart();
    this.createRenderers();
    this.createAxes();

    const legendNames: string[] = [];
    [
      ...Array.from(new Set(this.data.map((item: ChartDataRecord) => item[this.settings.decompositionMetric]))),
      ...this.emptyCategories,
    ].forEach((name: unknown, index: number) => {
      const x = Math.floor(index / 10);
      const seriesIndex = index - 10 * x;
      this.createSeries(name as string, seriesIndex);
      legendNames.push(name as string);
    });
    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData, false);
  }

  protected createChart() {
    const chart = XYChart.new(this.root, {
      ...chartInitialSettings,
      paddingTop: 0,
      panX: false,
      panY: false,
      layout: this.layout,
    });
    chart.zoomOutButton.set("forceHidden", true);

    this.chart = this.root.container.children.push(chart);
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, {
      minGridDistance: 10,
    });
    const y = AxisRendererY.new(this.root, {});

    x.labels.template.setAll({ forceHidden: true });
    y.labels.template.setAll({ forceHidden: true });

    this.renderers = { x, y };
  }

  protected createAxes() {
    const x = (this.chart as XYChart).xAxes.push(
      CategoryAxis.new(this.root, {
        categoryField: "x",
        renderer: this.renderers.x as AxisRendererX,
        tooltip: Tooltip.new(this.root, {}),
      })
    ) as CategoryAxis<AxisRendererX>;

    const y = (this.chart as XYChart).yAxes.push(
      CategoryAxis.new(this.root, {
        categoryField: "y",
        height: percent(100),
        renderer: this.renderers.y as AxisRendererY,
        tooltip: Tooltip.new(this.root, {}),
      })
    ) as CategoryAxis<AxisRendererY>;

    y.data.setAll(
      Array.from(Array(this.settings.yDimension).keys()).map((item: number, index: number) => ({ y: index + 1 }))
    );
    x.data.setAll(
      Array.from(Array(this.settings.xDimension).keys()).map((item: number, index: number) => ({ x: index + 1 }))
    );
    this.axes = { x, y };
  }

  protected createSeries(seriesName: string, index: number) {
    const color = this.getSeriesColor(index);
    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        name: seriesName,
        fill: color,
        xAxis: this.axes.x,
        yAxis: this.axes.y as CategoryAxis<AxisRendererY>,
        categoryYField: "y",
        openCategoryYField: "y",
        categoryXField: "x",
        openCategoryXField: "x",
        clustered: false,
      })
    );
    series.columns.template.setAll({
      width: percent(96),
      height: percent(92),
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
      cornerRadiusBL: 4,
      cornerRadiusBR: 4,
      tooltipText: "{name}, {categoryX}: {valueY}",
    });

    this.createTooltip(series as TooltipSeries, (target) => {
      let param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext as Record<string, unknown>;
      const name = series.get("name");
      if (tooltipValues && name) {
        param = [
          { key: this.settings.decompositionMetric, value: name },
          { key: this.settings.displayMetric, value: tooltipValues.value },
        ];
      }
      return param;
    });

    const currentCategory = this.data
      .filter((item: ChartDataRecord) => item[this.settings.decompositionMetric] === seriesName)
      .map((item: ChartDataRecord) => ({
        x: item.x,
        y: item.y,
        value: item.value,
      }));

    series.data.setAll(currentCategory);
  }

  changeValueScale(data: ChartDataRecord[], sumValue: number): GroupedPercentsBrickData[] {
    const seriesName = this.settings.displayMetric as string;
    const categoryName = this.settings.decompositionMetric as string;

    const averageBrickValue = sumValue / this.settings.xDimension / this.settings.yDimension;
    const MAX_CALCULATION_ERROR = 1e-5;

    data = data.sort((a, b) => Number(b[this.settings.displayMetric]) - Number(a[this.settings.displayMetric]));
    data = this.showItems(data as ChartDataRecord[], this.settings.decompositionMetric, this.settings.displayMetric);

    let dataWithPercents: PercentsBrickData[] = data.map((item) => ({
      ...item,
      percents: (((item[seriesName] || 0) as number) / averageBrickValue) * this.MAX_AMOUNT_OF_PERCENTS,
    }));

    dataWithPercents = dataWithPercents
      .filter((item) => item[this.settings.decompositionMetric] !== Lang.other)
      .sort((a, b) => b.percents - a.percents)
      .concat(dataWithPercents.filter((item) => item[this.settings.decompositionMetric] === Lang.other));

    const remainder: Record<string, number> = {};

    const bricks = new Array(this.settings.xDimension * this.settings.yDimension).fill(null).map((item, index) => {
      let sumItemsPercents = this.MAX_AMOUNT_OF_PERCENTS;
      const collectedData: GroupedPercentsBrickData = {};

      while (sumItemsPercents > MAX_CALCULATION_ERROR) {
        dataWithPercents[0].percents += remainder[dataWithPercents[0][categoryName]] || 0;
        remainder[dataWithPercents[0][categoryName]] = 0;

        if (dataWithPercents[0].percents > sumItemsPercents) {
          collectedData[dataWithPercents[0][categoryName]] = {
            percents: sumItemsPercents,
            data: dataWithPercents[0],
          };
          dataWithPercents[0].percents -= sumItemsPercents;
          sumItemsPercents = 0;
        } else {
          sumItemsPercents -= dataWithPercents[0].percents;
          collectedData[dataWithPercents[0][categoryName]] = {
            percents: dataWithPercents[0].percents,
            data: dataWithPercents[0],
          };
          dataWithPercents[0].percents = 0;
        }
        if (dataWithPercents[0].percents === 0) {
          dataWithPercents.shift();
        }
      }

      const maxValueKey = keys(collectedData).find(
        (key: string) => collectedData[key].percents >= this.MAX_AMOUNT_OF_PERCENTS / 2
      );
      if (maxValueKey) {
        keys(omit(collectedData, [maxValueKey])).forEach((key: string) => {
          remainder[key] = remainder[key] ? remainder[key] + collectedData[key].percents : collectedData[key].percents;
        });
      }
      if (
        !maxValueKey ||
        (index === this.settings.xDimension * this.settings.yDimension - 1 && dataWithPercents.length > 1)
      ) {
        return {
          [Lang.other]: {
            percents: this.MAX_AMOUNT_OF_PERCENTS,
            data: null,
          },
        };
      }
      return collectedData;
    });

    return bricks.reverse();
  }

  protected mapData(paramData: ChartDataRecord[]) {
    if (!paramData?.length) {
      return [];
    }

    if (!this.settings.xDimension) {
      this.settings.xDimension = waffleDefaults.xDimension;
    }

    if (!this.settings.yDimension) {
      this.settings.yDimension = waffleDefaults.yDimension;
    }

    const seriesName = this.settings.displayMetric as string;
    const categoryName = this.settings.decompositionMetric as string;
    paramData = paramData.map((item) => pick(item, [categoryName, seriesName]));
    const sumValue = paramData.reduce((a: number, b: ChartDataRecord) => a + Number(b[seriesName] || 0), 0);

    const seriesData = this.changeValueScale(paramData, sumValue);
    let seriesCoordinates = [];

    for (let i = 0; i < this.settings.xDimension; i++) {
      for (let j = 0; j < this.settings.yDimension; j++) {
        if (seriesData.length) {
          const { brickCategoryName, value } = this.fillBrick(seriesData.pop() as GroupedPercentsBrickData);
          seriesCoordinates.push({
            [categoryName]: brickCategoryName,
            value: Math.round(value as number),
            x: i + 1,
            y: j + 1,
          });
        } else {
          break;
        }
      }
    }

    const sumFilledValue = values(
      seriesCoordinates.reduce((acc, curr) => ({ ...acc, [curr[categoryName]]: curr.value }), {})
    ).reduce((acc, curr) => Number(acc) + Number(curr), 0) as number;
    seriesCoordinates = seriesCoordinates.map((item) =>
      item[categoryName] === Lang.other ? { ...item, value: sumValue - sumFilledValue } : { ...item }
    );

    return seriesCoordinates;
  }

  fillBrick(object: GroupedPercentsBrickData): { brickCategoryName: string | number; value: string | number | null } {
    const key = keys(object).find((key) => object[key].percents >= this.MAX_AMOUNT_OF_PERCENTS / 2);

    if (object[Lang.other]) {
      return {
        brickCategoryName: Lang.other,
        value: 0,
      };
    }

    return {
      brickCategoryName: object[key as string].data?.[this.settings.decompositionMetric] as string | number,
      value: object[key as string].data?.[this.settings.displayMetric] as string | number,
    };
  }
}
